import { useOutletContext } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import Decimal from 'decimal.js'
import { PerformanceReviewOverviewEmployeeScoreChartDocument } from 'generated/resources/graphql'
import { last, xor } from 'lodash'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { ReviewEmployeeScoreChart } from 'modules/performance_analytics/components/ReviewEmployeeScoreChart'
import { useFiltersValues } from 'modules/performance_process/components/review/show/Filters/context'
import { colorsForEmployeeScoreScaleId } from 'modules/performance_process/lib/employeeScoreColor'
import { useNewPerformanceModuleContext } from 'modules/performance_process/pages/ReviewsRootPage'
import { ReviewOverviewOutletContext } from 'modules/performance_process/pages/reviews/show/overview'

interface EmployeeScoreChartProps {
  search: string
  onChangeScores: (scores: number[]) => void
}

export const EmployeeScoreChart = ({ search, onChangeScores }: EmployeeScoreChartProps) => {
  const { reviewProcess } = useOutletContext<ReviewOverviewOutletContext>()
  const { hasCustomScoreConfigurationEnabled } = useNewPerformanceModuleContext()
  const employeeScoreScale = reviewProcess.scoreScale

  const {
    employeeIds,
    managerId,
    onlyDirectReports,
    agreementStatus,
    evaluationsStatus,
    teamIds,
    tenureDates,
    workplaceIds,
    legalEntityIds,
    scores,
  } = useFiltersValues()

  const { data, previousData, loading } = useQuery(
    PerformanceReviewOverviewEmployeeScoreChartDocument,
    {
      variables: {
        reviewProcessId: reviewProcess.id,
        employeeIds: employeeIds,
        managedByFilter: managerId
          ? {
              managerEmployeeId: managerId,
              onlyDirectReports: onlyDirectReports ?? true,
            }
          : undefined,
        search,
        agreementStatus,
        evaluationsStatus,
        teamIds,
        tenureDates,
        workplaceIds,
        legalEntityIds,
      },
    }
  )

  const chartData = data || previousData
  const aggregate = chartData?.performance?.reviewFinalEmployeeScoresConnection?.aggregate

  const total = aggregate?.count ?? 0
  const averageScore = new Decimal(aggregate?.average?.score ?? 0).toDecimalPlaces(2).toNumber()
  const scoreTerm = aggregate?.terms?.score ?? { count: [], values: [] }

  const colors = colorsForEmployeeScoreScaleId(employeeScoreScale.id)

  const barsData = employeeScoreScale.scale.map(({ text, value }, index) => {
    const scoreIndex = scoreTerm.values.findIndex((v) => Math.round(Number(v)) === value)

    return {
      bucket: text,
      score: scoreTerm.count?.[scoreIndex] ?? 0,
      color: colors[index] ?? 'grey400',
      __opacity: scores?.includes(value) || !scores?.length ? 1 : 0.3,
    }
  })

  return (
    <ReviewEmployeeScoreChart
      data={loading ? [] : barsData}
      margin={{ top: 20, bottom: 120, left: 30, right: -20 }}
      total={total}
      loading={loading}
      tooltip={i18n.t('performance_process.analytics.score_distribution_chart.description', {
        employees_count: total,
      })}
      onBarClick={(datum) => {
        const nextValue = xor(scores ?? [], [datum.index + 1])
        onChangeScores(nextValue)
      }}
      showEnterpriseUpsellingKit={!hasCustomScoreConfigurationEnabled}
    >
      <Box paddingBottom="s8">
        <Text.Normal size="200" weight="medium">
          {i18n.t('performance_process.reviews.score_average')}
        </Text.Normal>
        <Text.Header1 weight="semibold">
          {i18n.formatNumber(averageScore)}/{last(employeeScoreScale.scale)?.value}
        </Text.Header1>
      </Box>
    </ReviewEmployeeScoreChart>
  )
}
